import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { dashboardAPI, usersAPI, quoteRequestsAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatDate, getStatusColor, getTodayString } from '@/lib/utils';
import { 
  Loader2, 
  Route, 
  Truck, 
  AlertTriangle, 
  FileText,
  ArrowRight,
  Users,
  Calendar,
  Zap,
  Clock,
  Phone
} from 'lucide-react';

// Calculate urgency based on start date
const getQuoteUrgency = (startDate) => {
  if (!startDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const targetDate = new Date(startDate);
  const diffTime = targetDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) return 'past';
  if (diffDays < 2) return 'rush'; // Under 48 hours
  if (diffDays < 7) return 'short'; // 2-7 days
  return 'normal';
};

export default function AdminDashboard() {
  const { user, isDriver } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [urgentQuotes, setUrgentQuotes] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashResponse, driversResponse, quotesResponse] = await Promise.all([
        dashboardAPI.get(),
        usersAPI.getAll('driver'),
        quoteRequestsAPI.getAll('new'),
      ]);
      setDashboardData(dashResponse.data);
      setDrivers(driversResponse.data);
      
      // Filter for urgent quotes (rush or short notice)
      const urgent = (quotesResponse.data || []).filter(q => {
        const urgency = getQuoteUrgency(q.start_date);
        return urgency === 'rush' || urgency === 'short';
      });
      setUrgentQuotes(urgent);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#194461]" />
        </div>
      </AdminLayout>
    );
  }

  // If driver, redirect to driver view
  if (isDriver) {
    return (
      <AdminLayout title="Welcome Back">
        <Card className="border-slate-200">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-[#52B8E1]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Route className="text-[#52B8E1]" size={32} />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              Ready to Start Your Route?
            </h2>
            <p className="text-slate-600 mb-6">
              Access your daily route to see today's stops and update service status.
            </p>
            <Link to={`/admin/driver-view/${user?.id}/today`}>
              <Button className="bg-[#194461] hover:bg-[#091D2E]">
                View Today's Route
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </AdminLayout>
    );
  }

  const stats = dashboardData?.stats || {};
  const todayRoutes = dashboardData?.today_routes || [];
  const overdueUnits = dashboardData?.overdue_units || [];

  return (
    <AdminLayout title="Dashboard">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total Units</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="stat-total-units">
                  {stats.total_units || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-[#52B8E1]/10 rounded-xl flex items-center justify-center">
                <Truck className="text-[#52B8E1]" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">On Rent</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="stat-on-rent">
                  {stats.units_on_rent || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Truck className="text-emerald-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`border-slate-200 ${stats.overdue_count > 0 ? 'border-red-300 bg-red-50' : ''}`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Overdue Service</p>
                <p className={`text-2xl font-bold ${stats.overdue_count > 0 ? 'text-red-600' : 'text-slate-900'}`} data-testid="stat-overdue">
                  {stats.overdue_count || 0}
                </p>
              </div>
              <div className={`w-12 h-12 ${stats.overdue_count > 0 ? 'bg-red-100' : 'bg-slate-100'} rounded-xl flex items-center justify-center`}>
                <AlertTriangle className={stats.overdue_count > 0 ? 'text-red-600' : 'text-slate-400'} size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">New Quotes</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="stat-quotes">
                  {stats.pending_quotes || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <FileText className="text-blue-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Urgent Quotes Alert */}
      {urgentQuotes.length > 0 && (
        <Card className="mb-6 border-red-300 bg-gradient-to-r from-red-50 to-amber-50" data-testid="urgent-quotes-alert">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-red-700">
                <Zap size={20} className="text-red-500" />
                Urgent Quote Requests ({urgentQuotes.length})
              </CardTitle>
              <Link to="/admin/quote-requests">
                <Button variant="ghost" size="sm" className="text-red-700 hover:text-red-800 hover:bg-red-100">
                  View All <ArrowRight size={16} className="ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3">
              {urgentQuotes.slice(0, 3).map((quote) => {
                const urgency = getQuoteUrgency(quote.start_date);
                return (
                  <div 
                    key={quote.id}
                    className={`p-4 rounded-lg border ${
                      urgency === 'rush' 
                        ? 'bg-red-100 border-red-300' 
                        : 'bg-amber-100 border-amber-300'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <Link 
                        to="/admin/quote-requests"
                        className="flex-1 min-w-0 hover:opacity-80 transition-opacity"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          {urgency === 'rush' ? (
                            <Badge className="bg-red-500 text-white border-red-500">
                              <Zap size={12} className="mr-1" />
                              RUSH
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-500 text-white border-amber-500">
                              <Clock size={12} className="mr-1" />
                              SHORT NOTICE
                            </Badge>
                          )}
                          <span className="text-sm font-medium text-slate-700">
                            {formatDate(quote.start_date)}
                          </span>
                        </div>
                        <p className="font-semibold text-slate-900">{quote.customer_name}</p>
                        {quote.company_name && (
                          <p className="text-sm text-slate-600">{quote.company_name}</p>
                        )}
                        <p className="text-sm text-slate-600 mt-1">
                          {quote.units_requested} {quote.unit_type} unit{quote.units_requested > 1 ? 's' : ''} • {quote.site_address}
                        </p>
                      </Link>
                      <a 
                        href={`tel:${quote.phone}`}
                        className="flex-shrink-0"
                      >
                        <Button size="sm" className={urgency === 'rush' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}>
                          <Phone size={14} className="mr-1" />
                          Call
                        </Button>
                      </a>
                    </div>
                  </div>
                );
              })}
              {urgentQuotes.length > 3 && (
                <p className="text-center text-sm text-slate-600">
                  +{urgentQuotes.length - 3} more urgent request{urgentQuotes.length - 3 !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today's Routes */}
        <Card className="lg:col-span-2 border-slate-200">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Calendar size={20} className="text-[#52B8E1]" />
                Today's Routes
              </CardTitle>
              <Link to="/admin/routes">
                <Button variant="ghost" size="sm">
                  View All <ArrowRight size={16} className="ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {todayRoutes.length === 0 ? (
              <div className="text-center py-8">
                <Route className="mx-auto text-slate-300 mb-3" size={40} />
                <p className="text-slate-500">No routes scheduled for today</p>
                <Link to="/admin/routes">
                  <Button variant="outline" size="sm" className="mt-4">
                    Create Route
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {todayRoutes.map((route) => (
                  <Link 
                    key={route.id} 
                    to={`/admin/routes/${route.id}`}
                    className="block"
                    data-testid={`route-card-${route.id}`}
                  >
                    <div className="p-4 rounded-lg border border-slate-200 hover:border-[#52B8E1] hover:bg-slate-50 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-slate-900">{route.name}</h4>
                        <Badge className={getStatusColor(route.status)} variant="outline">
                          {route.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <Users size={14} />
                          {route.driver_name || 'Unassigned'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Truck size={14} />
                          {route.job_count} stops
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Overdue Units */}
        <Card className={`border-slate-200 ${overdueUnits.length > 0 ? 'border-red-200' : ''}`}>
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle size={20} className={overdueUnits.length > 0 ? 'text-red-500' : 'text-slate-400'} />
                Overdue Units
              </CardTitle>
              <Link to="/admin/units">
                <Button variant="ghost" size="sm">
                  View All <ArrowRight size={16} className="ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {overdueUnits.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Truck className="text-emerald-600" size={24} />
                </div>
                <p className="text-slate-500 text-sm">All units are up to date!</p>
              </div>
            ) : (
              <div className="space-y-2">
                {overdueUnits.slice(0, 5).map((unit) => (
                  <div 
                    key={unit.id}
                    className="p-3 rounded-lg bg-red-50 border border-red-200"
                    data-testid={`overdue-unit-${unit.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-900">{unit.unit_code}</span>
                      <Badge variant="outline" className="bg-red-100 text-red-700 border-red-200">
                        {unit.type}
                      </Badge>
                    </div>
                    <p className="text-xs text-red-600 mt-1">
                      Due: {formatDate(unit.next_service_date)}
                    </p>
                  </div>
                ))}
                {overdueUnits.length > 5 && (
                  <p className="text-center text-sm text-slate-500 pt-2">
                    +{overdueUnits.length - 5} more
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Driver Quick Links */}
      {drivers.length > 0 && (
        <Card className="mt-6 border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <Users size={20} className="text-[#52B8E1]" />
              Driver Views
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {drivers.map((driver) => (
                <Link 
                  key={driver.id}
                  to={`/admin/driver-view/${driver.id}/today`}
                  data-testid={`driver-link-${driver.id}`}
                >
                  <div className="p-4 rounded-lg border border-slate-200 hover:border-[#52B8E1] hover:bg-slate-50 transition-colors text-center">
                    <div className="w-10 h-10 rounded-full bg-[#194461] text-white flex items-center justify-center mx-auto mb-2 text-sm font-medium">
                      {driver.full_name.charAt(0)}
                    </div>
                    <p className="font-medium text-slate-900 text-sm">{driver.full_name}</p>
                    <p className="text-xs text-slate-500">View Route</p>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </AdminLayout>
  );
}
